<template>
  <v-row no-gutters>
    <v-col cols="12" class="pr-6">
      <card-title-row translation-path="sidebar.searchArea.title" class="pt-5" />
      <card-title-row translation-path="sidebar.searchArea.searchScope" is-subtitle class="pt-4" />
      <sidebar-row class="pt-2">
        <v-row no-gutters>
          <v-col cols="auto">
            <dots-menu
              :items="dotsItems"
              @multiple="turnOnDrawing('multiple')"
              @freehand="turnOnDrawing('freehand')"
              @single="turnOnDrawing('single')"
            >
              <template #activator="{ on }">
                <base-button
                  v-on="getButtonListeners(on)"
                  translation-path="sidebar.searchArea.selectArea"
                  :disabled="isDone || isStickyButtonsLoading"
                  :button-style="buttonStyle"
                />
              </template>
            </dots-menu>
          </v-col>
          <v-col v-if="isDone || !!selectedFeatures.length" cols="auto">
            <base-button
              translation-path="sidebar.searchArea.clearSelection"
              :disabled="isStickyButtonsLoading"
              cancel
              @click="clearSelection"
            />
          </v-col>
        </v-row>
        <italic-subheader translation-path="sidebar.searchArea.selectAreaSubtitle" class="text-left pt-1" />
      </sidebar-row>
      <sidebar-row v-if="isDone || !!selectedFeatures.length" class="pt-1">
        <data-input
          v-for="input in inputs.search"
          :key="input.name"
          v-model="payload[input.name]"
          v-bind="input"
          class="py-1"
        />
      </sidebar-row>
      <sidebar-row v-if="!!selectedFeatures.length" class="pt-1">
        <v-row justify="end" no-gutters>
          <v-col cols="auto">
            <clickable-text @click="deleteFeatures">
              {{ $i18n.t('sidebar.searchArea.deleteAll') }}
            </clickable-text>
          </v-col>
        </v-row>
        <ribbon-list :listItems="computedSelectedFeatures" style-type="raw" :toggleable="false">
          <template #itemTitle-prepend="{item}">
            {{ item.externalTool ? item.name : `${item.name}: ${item.descAttributeValue || ''}` }}
          </template>
        </ribbon-list>
      </sidebar-row>
      <card-title-row
        translation-path="sidebar.searchArea.landArea"
        is-subtitle
        :class="[isDone && !selectedFeatures.length ? 'pt-3' : 'pt-4']"
      />
      <sidebar-row class="pt-2">
        <v-row no-gutters>
          <v-col
            v-for="(input, idx) in inputs.area"
            cols="auto"
            class="d-flex align-center pa-0 input-area"
            :key="input.name"
          >
            <data-input v-model="payload[input.name]" v-bind="input" />
            <span class="ml-2">ha</span>
            <span v-if="idx === 0" class="mx-4">&mdash;</span>
          </v-col>
        </v-row>
      </sidebar-row>
      <card-title-row translation-path="sidebar.searchArea.excludedAreas" is-subtitle class="pt-4" />
      <sidebar-form v-model="params" :inputs="inputs.params" :is-editing-on="!isStickyButtonsLoading" />
      <sidebar-row class="pt-1">
        <v-row v-for="(layer, idx) in excludedLayers" :key="idx" no-gutters align="center" class="py-2">
          <v-col cols="auto">
            <data-input v-model="layer.checked" :data-type="{ name: 'checkbox' }" hide-details />
          </v-col>
          <v-col>
            <data-input
              v-model="excludedLayers[idx]"
              v-bind="inputs.exludedLayer"
              :items="getExcludedLayerItems(layer.id)"
              @change="handleExcludedLayerChange(layer.checked, idx, $event)"
            />
          </v-col>
          <v-col cols="auto" class="ml-5">
            <v-icon color="error" @click="deleteExcludedLayer(idx)">
              mdi-close
            </v-icon>
          </v-col>
        </v-row>
        <v-row no-gutters align="center" :class="!!excludedLayers.length ? 'pt-2' : 'pt-1'">
          <v-col cols="auto" style="width: 32px" />
          <v-col>
            <data-input
              v-model="newExcludedLayerId"
              v-bind="inputs.exludedLayer"
              :items="excludedLayersAvailable"
              clearable
            />
          </v-col>
          <v-col cols="auto" class="ml-5">
            <v-icon
              :color="isNewExcludedLayerValid ? 'success' : 'grey'"
              :style="`cursor: ${isNewExcludedLayerValid ? 'pointer' : 'default'}`"
              @click="pushExcludedLayer"
            >
              mdi-check
            </v-icon>
          </v-col>
        </v-row>
      </sidebar-row>
    </v-col>
  </v-row>
</template>

<script>
import { call, get, sync } from 'vuex-pathify';
import { createBox } from 'ol/interaction/Draw';
import { transformExtent } from 'ol/proj';

import CardTitleRow from '@/components/CardTitleRow';
import SidebarRow from '@/components/SidebarRow';
import SidebarForm from '@/components/SidebarForm';
import DataInput from '@/components/DataInput';
import DotsMenu from '@/components/DotsMenu';
import RibbonList from '@/components/RibbonList';
import ItalicSubheader from '@/components/ItalicSubheader';
import ClickableText from '@/components/ClickableText';

export default {
  name: 'SearchArea',
  components: {
    CardTitleRow,
    SidebarRow,
    SidebarForm,
    DataInput,
    DotsMenu,
    RibbonList,
    ItalicSubheader,
    ClickableText,
  },
  data: vm => ({
    isDone: false,
    isDrawing: false,
    selectedFeatures: [],
    sharedData: null,
    payload: {
      bbox: null,
      geojson: null,
      is_intersection: true,
      is_within: true,
    },
    params: {
      lasy: true,
      kowr: true,
      amw: true,
      pn: true,
      pk: true,
      oso: true,
      soo: true,
    },
    newExcludedLayerId: null,
    isNewExcludedLayerValid: false,
    excludedLayers: [],
    dotsItems: [
      { name: 'multiple' },
      { name: 'freehand' },
      { name: 'single', customName: vm.$i18n.t('menu.selectFeatures') },
    ],
  }),
  computed: {
    selectedFeaturesLayers: get('map/selectedFeatures'),
    layers: get('layers/layers'),
    projectElements: get('layers/project@layers'),
    projectLayers() {
      return this.$_getFlatGroupsLayers(this.projectElements).layers.filter(
        layer => !!layer.geometry_type && (layer.geometry_type === 'polygon' || layer.geometry_type === 'multipolygon')
      );
    },
    projectLayersMapped() {
      return this.projectLayers.map(layer => {
        return { id: layer.id, text: layer.name };
      });
    },
    stickyButtons: sync('sidebar/stickyButtons'),
    isStickyButtonsLoading: sync('sidebar/isStickyButtonsLoading'),
    isStickyButtonsVisible: sync('sidebar/isStickyButtonsVisible'),
    isStickyButtonsValid: sync('sidebar/isStickyButtonsValid'),
    moduleShareableData: get('moduleSharing/moduleSharingData'),
    buttonStyle() {
      return this.isDrawing
        ? {
            backgroundColor: `${this.$_colors.primarybackground} !important`,
            border: '1px solid rgba(26, 115, 232, 0.3) !important',
            color: this.$_colors.primary,
          }
        : {
            backgroundColor: 'rgba(0, 0, 0, 0.04) !important',
            border: '1px solid rgba(0, 0, 0, 0.2) !important',
            color: '#61646D',
          };
    },
    computedSelectedFeatures() {
      return this.selectedFeatures.map(feature => {
        return {
          ...feature,
          buttons: [
            {
              icon: 'mdi-magnify',
              tooltip: this.$i18n.t('sidebar.searchArea.zoomToFeature'),
              clickHandler: this.zoomToFeature,
            },
            {
              icon: 'mdi-close',
              params: { color: 'error' },
              tooltip: this.$i18n.t('sidebar.searchArea.deleteFeature'),
              clickHandler: this.deleteFeature,
            },
          ],
        };
      });
    },
    excludedLayersStorage() {
      return (JSON.parse(localStorage.getItem('searchAreaExcludedLayers')) || []).filter(layer =>
        this.projectLayersMapped.map(el => el.id).includes(layer.id)
      );
    },
    excludedLayersAvailable() {
      return this.projectLayersMapped.filter(layer => !this.excludedLayers.map(el => el.id).includes(layer.id));
    },
    inputs() {
      const defaultCheckboxParams = {
        dataType: { name: 'checkbox' },
        hasNameColumn: false,
        cols: '12',
        hideDetails: true,
      };
      return {
        search: [...(this.selectedFeatures?.length > 1 ? ['is_intersection'] : []), 'is_within'].map(input => {
          return {
            ...defaultCheckboxParams,
            name: input,
            label: this.$i18n.t(`sidebar.searchArea.${input}`),
          };
        }),
        area: ['area_ha_from', 'area_ha_to'].map(input => {
          return {
            name: input,
            dataType: { name: 'decimal', decimal_places: 3 },
            disabled: this.isStickyButtonsLoading,
            hideDetails: true,
          };
        }),
        params: ['lasy', 'kowr', 'amw', 'pn', 'pk', 'oso', 'soo'].map(input => {
          return {
            ...defaultCheckboxParams,
            name: input,
            label: this.$i18n.t(`sidebar.searchArea.${input}`),
            required: false,
          };
        }),
        exludedLayer: [
          {
            name: 'exludedLayer',
            dataType: { name: 'select' },
            label: this.$i18n.t('sidebar.searchArea.exludedLayer'),
            itemValue: 'id',
            hideDetails: true,
          },
        ],
      };
    },
  },
  watch: {
    'payload.bbox': {
      handler(nV) {
        this.isStickyButtonsValid = !!nV;
      },
    },
    'payload.geojson': {
      handler(nV) {
        this.isStickyButtonsValid = !!nV;
      },
    },
    selectedFeatures(nV) {
      if (this.sharedData) {
        const geojsonFeatures = nV
          .filter(item => !!item.geojsonFeatureMetadata)
          ?.map(item => item.geojsonFeatureMetadata);
        let parsedGeom = JSON.parse(this.sharedData.geometry);
        parsedGeom.features = geojsonFeatures;
        this.$root.$emit('toggleCustomVectorLayer', {
          id: `externalTool_${this.sharedData.tool}`,
          geometry: parsedGeom,
        });
      }
      this.isStickyButtonsValid = !!nV.length;
    },
    projectLayersMapped: {
      handler() {
        this.excludedLayers = this.excludedLayersStorage;
      },
      immediate: true,
      deep: true,
    },
    newExcludedLayerId(nV) {
      if (!!nV || nV === 0) this.isNewExcludedLayerValid = true;
      else this.isNewExcludedLayerValid = false;
    },
  },
  mounted() {
    if (this.moduleShareableData) {
      this.sharedData = JSON.parse(JSON.stringify(this.moduleShareableData));
      this.$store.set('moduleSharing/CLEAR_SHAREABLE_DATA!');
      const parsedGeom = JSON.parse(this.sharedData.geometry);
      this.selectedFeatures = parsedGeom.features.map(ft => ({
        geojsonFeatureMetadata: ft,
        name: this.$i18n.t('sidebar.searchArea.externalSource', {
          source: this.$i18n.t(`sidebar.searchArea.${this.sharedData.tool}Source`),
        }),
        externalTool: this.sharedData.tool,
      }));
      if (this.selectedFeatures.length) this.isDone = true;
    }
    this.$root.$off('search');
    this.$root.$on('search', this.search);
    this.stickyButtons = [
      {
        action: 'search',
        translationPath: 'sidebar.searchArea.search',
      },
    ];
    this.isStickyButtonsVisible = true;
  },
  beforeDestroy() {
    this.isStickyButtonsLoading = false;
    this.isStickyButtonsVisible = false;
    if (this.sharedData)
      this.$root.$emit('toggleCustomVectorLayer', {
        id: `externalTool_${this.sharedData.tool}`,
      });
    this.clearSelection();
  },
  methods: {
    getSearchedArea: call('externalSystems/getSearchedArea'),
    zoomToFeature({ item }) {
      if (item.geojsonFeatureMetadata?.geometry) {
        this.$root.$emit('createGeomAndFitView', item.geojsonFeatureMetadata.geometry);
      } else {
        console.log(item);
        this.$root.$emit('zoomToFeature', item.layerId, 'layers', item.id);
      }
    },
    deleteFeature({ item: { layerId, id: featureId, geojsonFeatureMetadata }, index }) {
      if (!geojsonFeatureMetadata?.geometry && featureId && layerId) {
        this.$store.set('map/DELETE_SELECTED_FEATURES!', {
          type: 'selectedFeatures',
          features: {
            [layerId]: [featureId],
          },
        });
      }
      this.selectedFeatures.splice(index, 1);
    },
    deleteFeatures() {
      for (const layerId of Object.keys(this.selectedFeaturesLayers)) {
        this.$store.set('map/DELETE_SELECTED_FEATURES!', {
          type: 'selectedFeatures',
          features: {
            [layerId]: [],
          },
        });
      }
      this.selectedFeatures = [];
    },
    clearSelection() {
      this.isDrawing = false;
      this.isDone = false;
      this.payload.bbox = null;
      this.payload.geojson = null;
      if (this.selectedFeatures.length) this.deleteFeatures();
      this.$root.$emit('clearSidebarGeometry');
      this.$root.$emit('identification-action', false);
    },
    getButtonListeners(on) {
      return this.isDrawing ? { click: this.turnOffSelecting } : on;
    },
    onDrawend(e, type) {
      this.isDrawing = false;
      this.isDone = true;
      if (type === 'multiple') {
        this.payload.bbox = transformExtent(
          e.feature.getGeometry().getExtent(),
          this.$_config.defaultEpsg,
          'EPSG:4326'
        );
      }
      if (type === 'freehand') {
        this.payload.geojson = {
          type: 'Polygon',
          crs: {
            type: 'name',
            properties: { name: 'EPSG:2180' },
          },
          coordinates: e.feature
            .getGeometry()
            .clone()
            .transform(this.$_config.defaultEpsg || 'EPSG:4326', 'EPSG:2180')
            .getCoordinates(),
        };
      }
    },
    turnOnDrawing(type) {
      this.isDrawing = true;
      if (type === 'single') {
        this.$root.$emit('identification-action', true, { specialType: 'addByMap' });
        this.$root.$off('assignedObjects');
        this.$root.$on('assignedObjects', identifiedFeatures => {
          const features = identifiedFeatures.map(feature => feature.features);
          for (const feature of features) {
            const { dataSource = '', layerId, id: featureId } = feature[0];
            if (!dataSource) return;
            const isFeatureSelected = this.selectedFeatures.some(el => el.layerId === layerId && el.id === featureId);
            if (!isFeatureSelected) this.selectedFeatures = this.selectedFeatures.concat(feature);
            this.$store.set('map/ADD_SELECTED_FEATURES!', {
              type: 'selectedFeatures',
              features: {
                [layerId]: [featureId],
              },
            });
            this.$root.$emit('setProjectLayerStyle', layerId);
          }
        });
      } else {
        this.$root.$emit('drawSidebarGeometry', {
          drawendCallback: e => this.onDrawend(e, type),
          geometryFunction: type === 'multiple' ? createBox() : null,
          geometryType: 'polygon',
          isInitSnapping: false,
          freehand: type === 'freehand' ? true : false,
          ...(type === 'multiple' ? { drawType: 'Circle' } : {}),
        });
      }
    },
    turnOffSelecting() {
      if (this.selectedFeatures.length) this.isDone = true;
      this.isDrawing = false;
      this.$root.$emit('selection-action');
    },
    getExcludedLayerItems(id) {
      const notExcludedLayers = this.projectLayersMapped.filter(
        layer => !this.excludedLayers.map(el => el.id).includes(layer.id)
      );
      const selectedLayer = this.projectLayersMapped.find(layer => layer.id === id);
      return [...notExcludedLayers, selectedLayer];
    },
    pushExcludedLayer() {
      if (!this.isNewExcludedLayerValid) return;
      const layer = this.projectLayersMapped.find(layer => layer.id === this.newExcludedLayerId);
      this.excludedLayers.push({ ...layer, checked: true });
      this.newExcludedLayerId = null;
    },
    deleteExcludedLayer(idx) {
      this.excludedLayers.splice(idx, 1);
    },
    handleExcludedLayerChange(checked, idx, newLayerId) {
      const newLayer = this.projectLayersMapped.find(layer => layer.id === newLayerId);
      this.excludedLayers[idx] = { ...newLayer, checked };
    },
    addLayerToProject(layerId) {
      const layer = this.layers[layerId];
      const isLayerInProject = this.projectLayersMapped.some(el => el.id === layerId);
      if (layer && !isLayerInProject) {
        layer.labels_visible = layer.labels_visible || false;
        layer.style = layer.style_web;
        delete layer.style_web;
        this.$store.set('layers/ADD_PROJECT_LAYERS!', [
          { layer_id: layer.id, visible: true, opacity: 1, has_permission: true, ...layer },
        ]);
        this.$root.$emit('pushProjectLayers', [
          { layer_id: layer.id, visible: true, opacity: 1, has_permission: true, ...layer },
        ]);
        this.$store.set('snackbar/PUSH_MESSAGE!', {
          message: this.$i18n.t('snackbar.layerAddedToProject', { name: layer.name }),
        });
      } else if (layer && isLayerInProject) {
        this.$root.$emit('refreshMvtSource', layerId);
        this.$root.$emit('updateLegendStylesCounts', { layersIds: [layerId] });
      }
    },
    async search() {
      this.isStickyButtonsLoading = true;
      try {
        const payload = JSON.parse(JSON.stringify(this.payload));
        Object.keys(this.params)
          .filter(key => this.params[key])
          .forEach(param => {
            payload[`area_ha_${param}_from`] = this.payload.area_ha_from;
            payload[`area_ha_${param}_to`] = this.payload.area_ha_to;
          });
        if (this.selectedFeatures.length) {
          const filteredExternalFeatures = this.selectedFeatures.filter(item => !!item.geojsonFeatureMetadata);
          if (filteredExternalFeatures.length > 0) {
            const geojsonFeatures = filteredExternalFeatures.map(item => item.geojsonFeatureMetadata);
            let parsedGeom = JSON.parse(this.sharedData.geometry);
            parsedGeom.features = geojsonFeatures;
            payload.geojson = parsedGeom;
          }
          payload.layers = [];
          for (const layerId in this.selectedFeaturesLayers) {
            if (this.selectedFeaturesLayers[layerId].length) {
              payload.layers.push({
                layer_id: layerId,
                ids: this.selectedFeaturesLayers[layerId],
              });
            }
          }
        } else {
          payload.is_intersection = false;
        }
        payload.excluded_layers = this.excludedLayers.filter(layer => layer.checked).map(layer => layer.id);
        localStorage.setItem('searchAreaExcludedLayers', JSON.stringify(this.excludedLayers));
        const layerId = await this.getSearchedArea(payload);
        this.addLayerToProject(layerId);
        this.clearSelection();
      } catch (error) {
        console.log('error: ', error);
      } finally {
        this.isStickyButtonsLoading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep {
  .input-area .v-input__control {
    width: 100px;
  }
  .v-input--checkbox .v-label {
    color: #202124;
  }
  .v-form > * {
    padding: 4px 0 !important;
    &:first-child {
      padding-top: 8px !important;
    }
  }
}
</style>
